import { useEffect, useRef, useState, useMemo } from "react";
import { motion, useInView, useReducedMotion, useScroll, useSpring, useMotionValue } from "framer-motion";

export function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const u = () => setM(mq.matches);
    u();
    mq.addEventListener("change", u);
    return () => mq.removeEventListener("change", u);
  }, []);
  return m;
}

/* 3D tilt card: follows cursor (max 6deg), lifts 4px, fuchsia glow. Mobile tap -> scale 0.97 + glow. */
export function TiltCard({ children, className, testid, max = 6 }) {
  const reduce = useReducedMotion();
  const rx = useSpring(0, { stiffness: 150, damping: 15 });
  const ry = useSpring(0, { stiffness: 150, damping: 15 });
  const [hover, setHover] = useState(false);
  const [tap, setTap] = useState(false);

  if (reduce) {
    return <div data-testid={testid} className={className}>{children}</div>;
  }

  const handleMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * max * 2);
    rx.set(-py * max * 2);
  };
  const reset = () => { rx.set(0); ry.set(0); setHover(false); };
  const glow = hover || tap;

  return (
    <motion.div
      data-testid={testid}
      onMouseEnter={() => setHover(true)}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      onTouchStart={() => setTap(true)}
      onTouchEnd={() => setTap(false)}
      onTouchCancel={() => setTap(false)}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 900, transformStyle: "preserve-3d" }}
      animate={{
        y: hover ? -4 : 0,
        scale: tap ? 0.97 : 1,
        boxShadow: glow ? "0 0 40px rgba(233,30,140,0.35)" : "0 0 0px rgba(233,30,140,0)",
      }}
      transition={{ type: "spring", stiffness: 220, damping: 22 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* Magnetic wrapper: element drifts toward cursor (max 8px) + tap bounce. */
export function Magnetic({ children, strength = 8, className = "inline-flex" }) {
  const reduce = useReducedMotion();
  const x = useSpring(0, { stiffness: 220, damping: 18, mass: 0.3 });
  const y = useSpring(0, { stiffness: 220, damping: 18, mass: 0.3 });

  const move = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - (r.left + r.width / 2);
    const my = e.clientY - (r.top + r.height / 2);
    const clamp = (v) => Math.max(-strength, Math.min(strength, v * 0.35));
    x.set(clamp(mx));
    y.set(clamp(my));
  };
  const reset = () => { x.set(0); y.set(0); };

  return (
    <motion.div
      className={className}
      style={{ x: reduce ? 0 : x, y: reduce ? 0 : y }}
      onMouseMove={move}
      onMouseLeave={reset}
      whileTap={reduce ? {} : { scale: 0.95 }}
    >
      {children}
    </motion.div>
  );
}

/* Light sweep that crosses a button on hover. Disabled under reduced motion via motion-safe. */
export function Sheen() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-y-0 left-0 w-1/3 -translate-x-[250%] skew-x-[-20deg] bg-gradient-to-r from-transparent via-white/30 to-transparent motion-safe:transition-transform motion-safe:duration-700 motion-safe:ease-out motion-safe:group-hover:translate-x-[450%]"
    />
  );
}

/* Soft fuchsia glow that follows the cursor inside its parent section. Mobile: appears on touch, fades in 0.6s. */
export function CursorGlow({ size = 300 }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const x = useMotionValue(-9999);
  const y = useMotionValue(-9999);
  const sx = useSpring(x, { stiffness: 120, damping: 22, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 120, damping: 22, mass: 0.4 });
  const [op, setOp] = useState(0);

  useEffect(() => {
    if (reduce) return;
    const el = ref.current?.parentElement;
    if (!el) return;
    let t;
    const place = (cx, cy) => {
      const r = el.getBoundingClientRect();
      x.set(cx - r.left - size / 2);
      y.set(cy - r.top - size / 2);
    };
    const move = (e) => { place(e.clientX, e.clientY); setOp(0.15); };
    const leave = () => setOp(0);
    const touch = (e) => {
      const p = e.touches[0];
      place(p.clientX, p.clientY);
      setOp(0.15);
      clearTimeout(t);
      t = setTimeout(() => setOp(0), 600);
    };
    el.addEventListener("mousemove", move);
    el.addEventListener("mouseleave", leave);
    el.addEventListener("touchstart", touch, { passive: true });
    el.addEventListener("touchmove", touch, { passive: true });
    return () => {
      clearTimeout(t);
      el.removeEventListener("mousemove", move);
      el.removeEventListener("mouseleave", leave);
      el.removeEventListener("touchstart", touch);
      el.removeEventListener("touchmove", touch);
    };
  }, [reduce, size, x, y]);

  if (reduce) return null;
  return (
    <motion.div
      ref={ref}
      aria-hidden
      className="rounded-full blur-3xl"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 0,
        width: size,
        height: size,
        x: sx,
        y: sy,
        opacity: op,
        background: "radial-gradient(circle, rgba(233,30,140,0.6), transparent 70%)",
        transition: "opacity 0.6s ease",
      }}
    />
  );
}

/* Slow zoom on hover (desktop) or on enter-viewport (mobile) + subtle lighten overlay. */
export function ZoomImage({ src, alt, wrapperClassName = "", imgClassName = "", style }) {
  const reduce = useReducedMotion();
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-12%" });
  const [hover, setHover] = useState(false);
  const zoom = !reduce && (hover || (isMobile && inView));
  return (
    <div
      ref={ref}
      className={`relative overflow-hidden ${wrapperClassName}`}
      style={style}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        animate={{ scale: zoom ? 1.06 : 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={imgClassName}
      />
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none bg-white"
        animate={{ opacity: zoom ? 0.07 : 0 }}
        transition={{ duration: 0.8 }}
      />
    </div>
  );
}

const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 40, className }) {
  const reduce = useReducedMotion();
  if (reduce) {
    return <div className={className}>{children}</div>;
  }
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function WordReveal({ text, className, el = "h2", delayStep = 0.05, base = 0 }) {
  const reduce = useReducedMotion();
  const El = el;
  if (reduce) {
    return <El className={className}>{text}</El>;
  }
  const words = text.split(" ");
  return (
    <El className={className}>
      {words.map((w, i) => (
        <motion.span
          key={i}
          className="inline-block"
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: base + i * delayStep, ease: EASE }}
        >
          {w}
          {i < words.length - 1 ? "\u00A0" : ""}
        </motion.span>
      ))}
    </El>
  );
}

export function ScrollProgress() {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  if (reduce) return null;
  return (
    <motion.div
      data-testid="scroll-progress"
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 h-[3px] bg-[#E91E8C] origin-left z-[60] pointer-events-none"
    />
  );
}

export function CountUp({ to, suffix = "", duration = 1.2 }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (reduce) {
      setVal(to);
      return;
    }
    if (!inView) return;
    let start;
    let raf;
    const step = (t) => {
      if (start === undefined) start = t;
      const p = Math.min((t - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration, reduce]);

  return <span ref={ref}>{val}{suffix}</span>;
}

export function Particles({ count = 18 }) {
  const items = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: 40 + Math.random() * 60,
        size: 3 + Math.random() * 7,
        duration: 7 + Math.random() * 9,
        delay: Math.random() * 8,
      })),
    [count]
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {items.map((p) => (
        <span
          key={p.id}
          className="particle"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </div>
  );
}
